import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { SaveState } from "@/stores/SaveDrawingState";
import Settings from "@/stores/Settings";

export const SAVE_REMINDER_DELAY_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Délai réduit quand la sauvegarde automatique est désactivée : le rappel est
 * alors le SEUL signal qui reste à l'utilisateur avant la fermeture du dessin.
 */
export const SAVE_REMINDER_DELAY_NO_AUTOSAVE_MS = 2 * 60 * 1000; // 2 minutes

export function getSaveReminderDelayMs(): number {
  return Settings.autoSaveOn
    ? SAVE_REMINDER_DELAY_MS
    : SAVE_REMINDER_DELAY_NO_AUTOSAVE_MS;
}

// Module-level state shared between the two exports (mirrors useMemoryWarning.ts).
const needsReminder = ref(false);

/**
 * Écarté par l'utilisateur : la portée est la **session applicative**. Le drapeau
 * n'est jamais remis à zéro en cours de route — ni par une sauvegarde, ni par une
 * nouvelle série de modifications, ni en changeant de dessin — seul un
 * rechargement de la PWA le réinitialise (état module-level, non persisté).
 * Choix délibéré : quelqu'un qui écarte ce badge sait ce qu'il fait et ne veut
 * plus le voir de la journée. Ne pas « corriger » en le réarmant.
 */
const dismissed = ref(false);

export const useGetSaveReminderInfo = () => {
  const showBanner = computed(() => needsReminder.value && !dismissed.value);

  function dismiss() {
    dismissed.value = true;
  }

  return { showBanner, dismiss };
};

/**
 * Watches SaveState and arms a one-shot timer that is NOT reset by further edits
 * (unlike the autosave timer) — the whole point is to catch a continuous
 * editing streak that never lets the autosave timer fire, and to be the only
 * remaining signal when autosave is turned off. Exported standalone
 * (independent of onMounted/onUnmounted) so it can be unit-tested without a
 * mounted component; useSaveReminder() below wires it to AuthLayout.vue's lifecycle.
 */
export function armSaveReminderWatch(): () => void {
  let reminderTimer: ReturnType<typeof setTimeout> | null = null;

  function clearReminderTimer() {
    if (reminderTimer) {
      clearTimeout(reminderTimer);
      reminderTimer = null;
    }
  }

  const stopWatch = watch(
    SaveState,
    (state, previous) => {
      if (state === "changed" && previous !== "changed") {
        clearReminderTimer();
        reminderTimer = setTimeout(() => {
          needsReminder.value = true;
        }, getSaveReminderDelayMs());
      } else if (state !== "changed") {
        // "saved" (a save just completed, auto or manual) or "saving"
        clearReminderTimer();
        needsReminder.value = false;
      }
    },
    // React to every SaveState transition immediately rather than on Vue's
    // batched "pre" flush — we're arming/disarming a plain timer here, not
    // touching the DOM, so there's no benefit to deferring it, and sync flush
    // means the timer start point never drifts from the actual edit moment.
    { flush: "sync" }
  );

  return () => {
    clearReminderTimer();
    stopWatch();
  };
}

export const useSaveReminder = () => {
  onMounted(() => {
    const stop = armSaveReminderWatch();
    onUnmounted(stop);
  });
};
